import { getUserFacingApiMessage } from "../../utils/getUserFacingApiMessage";

/**
 * @param {ReturnType<typeof getUserFacingApiMessage>} u
 * @param {{ title: string; body: string }} obj
 */
function withDebug(u, obj) {
  if (u?.integrationDebug) {
    return { ...obj, integrationDebug: u.integrationDebug };
  }
  return obj;
}

/**
 * Normaliza erros de axios / fetchData ao conectar WhatsApp pessoal e monta textos para o usuário.
 * @param {unknown} err
 * @param {import("i18next").TFunction} t - useTranslation("profile").t
 * @param {{ mode?: "connection" | "disconnect" }} [options]
 * @returns {{ title: string; body: string; integrationDebug?: object }}
 */
export function personalWhatsappErrorToStrings(err, t, options = {}) {
  const mode = options.mode === "disconnect" ? "disconnect" : "connection";
  const u = getUserFacingApiMessage(err);
  const { status, code, message, isNetwork } = extractPersonalWaApiError(err);
  const detailRaw = String(u.message || message || "").trim();
  const detail = detailRaw || t("personalWhatsapp.errors.noDetail");

  if (isNetwork) {
    return withDebug(u, {
      title: t("personalWhatsapp.errors.networkTitle"),
      body: t("personalWhatsapp.errors.networkBody"),
    });
  }

  if (status === 402) {
    return withDebug(u, {
      title: t("personalWhatsapp.errors.billingTitle"),
      body: t("personalWhatsapp.errors.billingBody", { detail }),
    });
  }

  if (status === 401 || code === "unauthorized") {
    return withDebug(u, {
      title: t("personalWhatsapp.errors.unauthorizedTitle"),
      body: t("personalWhatsapp.errors.unauthorizedBody"),
    });
  }

  const isCreateFailed =
    code === "personal_whatsapp.create_failed" || code === "errors.personal_whatsapp.create_failed";
  if (isCreateFailed) {
    const bodyText = u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.createFailedBody", { detail });
    return withDebug(u, {
      title: t("personalWhatsapp.errors.createFailedTitle"),
      body: bodyText,
    });
  }

  const isNoSession =
    code === "personal_whatsapp.no_session" || code === "errors.personal_whatsapp.no_session";
  if (isNoSession) {
    const bodyText = u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.noSessionBody");
    return withDebug(u, {
      title: t("personalWhatsapp.errors.noSessionTitle"),
      body: bodyText,
    });
  }

  const isQrFailed = code === "personal_whatsapp.qr_failed" || code === "errors.personal_whatsapp.qr_failed";
  if (isQrFailed) {
    const bodyText = u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.qrFailedBody", { detail });
    return withDebug(u, {
      title: t("personalWhatsapp.errors.qrFailedTitle"),
      body: bodyText,
    });
  }

  if (status != null && status >= 500) {
    if (mode === "disconnect") {
      return withDebug(u, {
        title: t("personalWhatsapp.errors.disconnectFailedTitle"),
        body: u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.disconnectFailedBody", { detail }),
      });
    }
    return withDebug(u, {
      title: t("personalWhatsapp.errors.serverErrorTitle"),
      body: u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.serverErrorBody", { detail }),
    });
  }

  if (mode === "disconnect") {
    return withDebug(u, {
      title: t("personalWhatsapp.errors.disconnectFailedTitle"),
      body: u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.disconnectFailedBody", { detail }),
    });
  }

  return withDebug(u, {
    title: t("personalWhatsapp.errors.unknownTitle"),
    body: u.action ? `${u.message}\n\n${u.action}` : t("personalWhatsapp.errors.unknownBody", { detail }),
  });
}

function extractPersonalWaApiError(err) {
  if (err && typeof err === "object" && err.response == null && typeof err.message === "string") {
    const status = err.status != null ? Number(err.status) : null;
    const code = typeof err.code === "string" ? err.code : null;
    if (status === 402 || status === 401 || code) {
      return { status, code, message: err.message, isNetwork: false };
    }
  }

  const r = err && typeof err === "object" ? err.response : null;
  const data = r && typeof r.data === "object" && r.data != null ? r.data : null;
  const status = r != null && r.status != null ? Number(r.status) : null;
  const code =
    (data && typeof data.code === "string" ? data.code : null) ||
    (err && typeof err.code === "string" && !/^ERR_/.test(err.code) ? err.code : null);
  const message =
    (data && typeof data.message === "string" && data.message) ||
    (err && typeof err.message === "string" ? err.message : "") ||
    "";

  const isNetwork =
    !r &&
    err &&
    typeof err === "object" &&
    (err.code === "ERR_NETWORK" ||
      err.code === "ECONNABORTED" ||
      (typeof err.message === "string" && /network/i.test(err.message)));

  return { status, code, message, isNetwork: Boolean(isNetwork) };
}

/** Resposta OK mas sem imagem de QR (provedor atrasado ou payload inesperado). */
export function personalWhatsappQrEmptyStrings(t) {
  return {
    title: t("personalWhatsapp.errors.qrEmptyTitle"),
    body: t("personalWhatsapp.errors.qrEmptyBody"),
  };
}
